import { Link } from "react-router-dom";
import StoryCard from "@/components/feature/StoryCard";
import { categories } from "@/mocks/categories";
import type { Story } from "@/mocks/stories";

type TrackFlowSectionProps = {
  feed: Story[];
  reversals: Story[];
  tags: string[];
};

export default function TrackFlowSection({ feed, reversals, tags }: TrackFlowSectionProps) {
  return (
    <section className="bg-background-100 py-12 md:py-16">
      <div className="mx-auto max-w-[1360px] px-4 md:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <div className="mb-5 flex items-end justify-between gap-4 border-b border-background-300 pb-4">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                最新追踪流
              </h2>
              <span className="font-label text-xs text-foreground-500 md:text-sm">
                按更新时间倒序
              </span>
            </div>
            <div>
              {feed.map((story) => (
                <StoryCard key={story.id} story={story} variant="row" />
              ))}
            </div>
            <Link
              to="/news"
              className="mt-6 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-list-check-2" />
              浏览全部追踪记录
            </Link>
          </div>

          <aside className="flex flex-col gap-6 lg:col-span-4">
            <div className="rounded-lg border border-accent-200 bg-accent-50 p-4 md:p-5">
              <h3 className="mb-3 flex items-center gap-2 border-b border-accent-200 pb-3 font-heading text-base font-bold text-accent-900">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-loop-left-line" />
                </span>
                最近的反转现场
              </h3>
              <ul className="flex flex-col gap-3">
                {reversals.map((story) => (
                  <li key={story.id}>
                    <Link
                      to={`/news/${story.id}`}
                      className="group block border-b border-accent-200/70 pb-3 last:border-b-0 last:pb-0"
                    >
                      <span className="line-clamp-2 font-heading text-sm font-medium leading-snug text-accent-950 transition-colors group-hover:text-primary-600">
                        {story.title}
                      </span>
                      <span className="mt-1 block font-label text-xs text-accent-700">
                        {story.timeline.length} 个节点 · {story.date}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-300 bg-background-50 p-4 md:p-5">
              <h3 className="mb-3 border-b border-background-200 pb-3 font-heading text-base font-bold text-foreground-950">
                主题分类
              </h3>
              <ul className="flex flex-col gap-1.5">
                {categories.map((cat) => (
                  <li key={cat.id}>
                    <Link
                      to={`/news?category=${encodeURIComponent(cat.id)}`}
                      className="group flex items-center justify-between gap-3 rounded-md px-2 py-2 transition-colors hover:bg-background-100"
                    >
                      <span className="flex min-w-0 items-center gap-2.5">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-200 text-foreground-700">
                          <i className={`${cat.icon} text-sm`} />
                        </span>
                        <span className="truncate font-label text-sm text-foreground-800 group-hover:text-primary-600">
                          {cat.name}
                        </span>
                      </span>
                      <span className="shrink-0 font-label text-xs text-foreground-500">
                        {cat.count}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-300 bg-background-50 p-4 md:p-5">
              <h3 className="mb-3 border-b border-background-200 pb-3 font-heading text-base font-bold text-foreground-950">
                热门标签
              </h3>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/news?q=${encodeURIComponent(tag)}`}
                    className="rounded-md bg-secondary-50 px-2.5 py-1 font-label text-xs text-secondary-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}